export type ChatStreamHandlers = {
	onDelta: (content: string) => void
	onSession: (sessionId: string) => void
}

/**
 * Como a resposta terminou:
 * - done: o backend enviou o evento "done".
 * - error: o backend enviou o evento "error" (timeout dele, falha do modelo, resposta vazia).
 * - interrupted: o stream acabou sem "done" nem "error" (conexão caiu no meio).
 * - timeout: o limite do cliente estourou sem o backend responder.
 */
export type ChatStreamEnd =
	| { kind: 'done' }
	| { kind: 'error'; message: string }
	| { kind: 'interrupted' }
	| { kind: 'timeout' }

// O backend corta a resposta em 120 s e devolve um evento "error"; este limite só cobre a
// conexão que caiu sem aviso, para o indicador de carregamento nunca ficar girando para sempre.
export const STREAM_TIMEOUT_MS = 150_000

export class ChatStreamProvider {
	/**
	 * Abre o stream com `open` e repassa os eventos SSE do WalterIA aos handlers.
	 *
	 * Lança quando o stream nem chega a começar (rede, HTTP != 2xx), para quem chama cair no
	 * fallback sem streaming. Depois de começar, nunca lança: devolve como terminou, e quem
	 * chama decide a mensagem. O timeout também não lança, senão o fallback repetiria a pergunta.
	 */
	static async run(
		open: (signal: AbortSignal) => Promise<Response>,
		handlers: ChatStreamHandlers,
		timeoutMs = STREAM_TIMEOUT_MS,
	): Promise<ChatStreamEnd> {
		const controller = new AbortController()
		const timer = setTimeout(() => controller.abort(), timeoutMs)
		try {
			const response = await open(controller.signal)
			if (!response.ok || !response.body) throw new Error(`Stream failed: ${response.status}`)
			return await this.read(response.body, handlers)
		} catch (e) {
			if (controller.signal.aborted) return { kind: 'timeout' }
			throw e
		} finally {
			clearTimeout(timer)
		}
	}

	static async read(body: ReadableStream<Uint8Array>, handlers: ChatStreamHandlers): Promise<ChatStreamEnd> {
		const reader = body.getReader()
		const decoder = new TextDecoder()
		let buffer = ''
		// Fora do laço: "event:" e "data:" podem chegar em pedaços diferentes da rede.
		let eventType = ''

		while (true) {
			const { done, value } = await reader.read()
			if (done) break
			buffer += decoder.decode(value, { stream: true })
			const lines = buffer.split('\n')
			buffer = lines.pop() || ''

			for (const rawLine of lines) {
				const line = rawLine.replace(/\r$/, '')
				if (line.startsWith('event: ')) {
					eventType = line.slice(7).trim()
					continue
				}
				if (!line.startsWith('data: ')) continue
				const data = line.slice(6)
				if (data === '[DONE]') continue

				let parsed: { content?: string; sessionId?: string; message?: string }
				try {
					parsed = JSON.parse(data)
				} catch {
					continue
				}

				if (eventType === 'error') {
					return { kind: 'error', message: parsed.message || '' }
				}
				if (parsed.sessionId) handlers.onSession(parsed.sessionId)
				if (eventType === 'done') return { kind: 'done' }
				// Sem "event:" (formato antigo), todo "data" com content é texto da resposta.
				if ((eventType === 'delta' || !eventType) && parsed.content) handlers.onDelta(parsed.content)
			}
		}

		return { kind: 'interrupted' }
	}
}
